// My Jobs Search *********************************************************************************
// pathname: /v1/my-jobs
// ************************************************************************************************

import { useSwrInfiniteApi, GenericV1ErrorsArray } from '@hooks/useSwrApi';

// Interface
// ********************************************
interface MyJobsSearchSwrProps {
  locale?: string;
  serverSideData?: ApiSchema<'MyJobResult'> | GenericV1ErrorsArray;
  startIndex?: number;
  limit?: number;
  urlParams?: string;
  onSuccess?: (data?: ApiSchema<'MyJobResult'>[]) => void;
}

/**
 * @description SWR: fetch my-jobs v1 with infinite pagination.
 *
 * * **locale**: string. What locale to use for the request.
 * * **serverSideData**: ApiSchema<'MyJobResult'>, optional. The data we got from the server side request (if we do it). Default undefined.
 * * **startIndex**: number, optional. The first page number to fetch (ie: fetch starting with page 5). Default 1.
 * * **limit**: number, optional. The number of jobs to get per call. Modifies the *limit* url parameter (&limit=24). Default 24.
 * * **restUrlParams**: '&sort=relevant', optional. Default empty string.
 */
export const useSwrMyJobsSearch = (props: MyJobsSearchSwrProps) => {
  // Props destructuring
  // ********************************************
  const {
    locale,
    serverSideData,
    startIndex = 1,
    limit = 24,
    urlParams = '',
    onSuccess,
  } = props;

  // Get data from API
  // ********************************************
  const {
    data,
    error,
    size,
    setSize,
    isValidating,
    isLoading,
    mutate,
    mutatePage,
    isEmpty,
    showLoadMore,
    sizeAsRef,
  } = useSwrInfiniteApi<ApiSchema<'MyJobResult'>>({
    url: '/v1/my-jobs',
    locale,
    urlParams,
    serverSideData,
    infiniteConfig: {
      paginationType: 'page',
      limit,
      // the key where the array of items is located in the response
      responseArrayKey: 'items',
      startIndex,
    },
    apiOptions: {
      keepPreviousData: true,
      revalidateOnFocus: false,
      dedupingInterval: 1000 * 2, // 2 seconds
      onSuccess: (newData) => {
        onSuccess?.(newData);
      },
    },
    toastsOptions: {
      showErrorToast: true,
    },
  });


  // Return data
  // ********************************************
  return {
    data,
    error,
    size,
    setSize,
    isValidating,
    isLoading,
    mutate,
    mutatePage,
    isEmpty,
    // We need to account for the case when the data the last page perfectly fits the limit, so we use the pages property
    // if we reached the end (page === pages), we don't show the load more button (showLoadMore = false)
    showLoadMore: showLoadMore && data?.at(-1)?.page !== data?.at(-1)?.pages,
    sizeAsRef,
  };
};
